import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { CompanyType, GetCompanyDetailsQuery } from '../../../__generated__/graphql'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import IconButton from '@mui/material/IconButton'
import { useIsMobileLayout } from '../../../utils/hooks/useIsMobileLayout'
import CloseIcon from '@mui/icons-material/Close'
import MenuWithIcon, { MenuActionItem } from '../MenuWithIcon'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useCompanyDetailsContext } from '../../../utils/hooks/useCompanyDetailsContext'
import { sidebarPalette, sidebarTextColor } from './sidebarStyles'
import CreateWebsite from '../../../pages/website/create/CreateWebsite'
import { FormDialog } from '../FormDialog'
import CreateCampaign from '../../../pages/campaign/CreateCampaign'
import {
  AdvertiserRouteBuilder,
  TradingDeskRouteBuilder,
  PublisherRouteBuilder,
  AgencyRouteBuilder,
} from '../../../pages/routeParams'
import { NoUnderlineLink } from '../styled'
import styled from '@emotion/styled'
import type { Item } from './Sidebar'
import { useSidebarContext } from '../../../utils/providers/sidebarContext'
import { CompanyNavigationState } from '../../../utils/hooks/useNavigation'

const StyledListItemText = styled(ListItemText)(() => ({
  fontSize: '1.4rem',
  textTransform: 'uppercase',
  fontWeight: 600,
  color: sidebarTextColor,
}))

export const SidebarHeader: React.FC<{
  updateWithArchived: (showArchived: boolean) => void
  items: Item[]
}> = ({ updateWithArchived, items }): JSX.Element => {
  const t = useAppTranslations()
  const { company } = useCompanyDetailsContext()
  const companyTypesWithoutMenu: Array<CompanyType> = [CompanyType.Reseller]
  const [showArchived, setShowArchived] = useState<boolean>(false)

  useEffect(() => {
    updateWithArchived(showArchived)
  }, [showArchived])

  if (companyTypesWithoutMenu.includes(company.type)) return <></>

  return (
    <SidebarHeaderContent
      currentCompany={company}
      items={items}
      archiveText={t(`layout.sidebar.${showArchived ? 'hideArchived' : 'showArchived'}`)}
      toggleArchived={() => setShowArchived(!showArchived)}
    />
  )
}

const SidebarHeaderContent: React.FC<{
  currentCompany: GetCompanyDetailsQuery['company']
  items: Item[]
  toggleArchived: () => void
  archiveText: string
}> = ({ currentCompany, toggleArchived, archiveText, items }) => {
  if (currentCompany.type === CompanyType.Agency)
    return (
      <StyledHeader link={`${AgencyRouteBuilder(currentCompany.slug)}/details`}>{currentCompany.name}</StyledHeader>
    )
  if (currentCompany.type === CompanyType.Publisher)
    return (
      <>
        <PublisherSidebarHeaderContent
          currentCompany={currentCompany}
          items={items}
          toggleArchived={toggleArchived}
          archiveText={archiveText}
        />
      </>
    )

  if (currentCompany.type === CompanyType.Advertiser || currentCompany.type === CompanyType.TradingDesk)
    return (
      <>
        <AdvertiserSidebarHeaderContent
          currentCompany={currentCompany}
          items={items}
          toggleArchived={toggleArchived}
          archiveText={archiveText}
        />
      </>
    )

  return <></>
}

const StyledHeader: React.FC<{ link?: string; actions?: MenuActionItem[] }> = ({ link, actions, children }) => {
  const isMobile = useIsMobileLayout()
  const { close } = useSidebarContext()
  const t = useAppTranslations()

  return (
    <ListItem
      data-testid="sidebarHeaderContent"
      secondaryAction={
        actions && actions.length > 0 ? (
          <MenuWithIcon edge="end" actionItems={actions} dataId="sidebarMenuIcon">
            <MoreVertIcon fontSize="small" />
          </MenuWithIcon>
        ) : null
      }
      sx={{
        backgroundColor: sidebarPalette.dark,
        minHeight: '6.6rem',
      }}
    >
      {isMobile && (
        <IconButton
          aria-label={t('layout.sidebar.close')}
          data-testid="sidebarMobileClose"
          onClick={close}
          sx={{ color: sidebarTextColor, mr: 1 }}
        >
          <CloseIcon />
        </IconButton>
      )}
      {link ? (
        <NoUnderlineLink to={link}>
          <StyledListItemText>{children}</StyledListItemText>
        </NoUnderlineLink>
      ) : (
        <StyledListItemText>{children}</StyledListItemText>
      )}
    </ListItem>
  )
}

const AdvertiserSidebarHeaderContent: React.FC<{
  currentCompany: GetCompanyDetailsQuery['company']
  items: Item[]
  toggleArchived: () => void
  archiveText: string
}> = ({ currentCompany, toggleArchived, archiveText, items }) => {
  const t = useAppTranslations()
  const location = useLocation()
  const navigate = useNavigate()
  const companyNavigationState = location.state as CompanyNavigationState | null
  const [createCampaignDialogOpened, setCreateCampaignDialogOpened] = useState(
    companyNavigationState?.openCreate === 'campaign'
  )

  useEffect(() => {
    if (companyNavigationState?.openCreate !== 'campaign') return
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null })
  }, [companyNavigationState?.openCreate, location.pathname, location.search, navigate])

  const advertiserActionItems = useMemo(() => {
    const defaultMenu: MenuActionItem[] = [
      {
        text: t('layout.sidebar.advertiser.createCampaign'),
        action: () => setCreateCampaignDialogOpened(true),
        autoClose: true,
      },
    ]

    return defaultMenu.concat([
      {
        text: archiveText,
        action: toggleArchived,
      },
    ])
  }, [items])

  const link =
    currentCompany.type === CompanyType.TradingDesk
      ? TradingDeskRouteBuilder(currentCompany.slug)
      : AdvertiserRouteBuilder(currentCompany.slug)

  return (
    <>
      <StyledHeader link={link} actions={advertiserActionItems}>
        {currentCompany.name}
      </StyledHeader>

      <FormDialog
        title={t('campaign.create')}
        open={createCampaignDialogOpened}
        setOpen={setCreateCampaignDialogOpened}
      >
        <CreateCampaign setOpen={setCreateCampaignDialogOpened} />
      </FormDialog>
    </>
  )
}

const PublisherSidebarHeaderContent: React.FC<{
  currentCompany: GetCompanyDetailsQuery['company']
  items: Item[]
  toggleArchived: () => void
  archiveText: string
}> = ({ currentCompany, toggleArchived, archiveText, items }) => {
  const t = useAppTranslations()
  const location = useLocation()
  const navigate = useNavigate()
  const companyNavigationState = location.state as CompanyNavigationState | null
  const [createWebsiteDialogOpened, setCreateWebsiteDialogOpened] = useState(
    companyNavigationState?.openCreate === 'website'
  )

  useEffect(() => {
    if (companyNavigationState?.openCreate !== 'website') return
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null })
  }, [companyNavigationState?.openCreate, location.pathname, location.search, navigate])

  const publisherActionItems = useMemo(() => {
    const defaultMenu: MenuActionItem[] = [
      {
        text: t('layout.sidebar.publisher.createWebsite'),
        action: () => setCreateWebsiteDialogOpened(true),
        autoClose: true,
      },
    ]

    return defaultMenu.concat([
      {
        text: archiveText,
        action: toggleArchived,
      },
    ])
  }, [items])

  return (
    <>
      <StyledHeader link={PublisherRouteBuilder(currentCompany.slug)} actions={publisherActionItems}>
        {currentCompany.name}
      </StyledHeader>

      <FormDialog title={t('website.create')} open={createWebsiteDialogOpened} setOpen={setCreateWebsiteDialogOpened}>
        <CreateWebsite setOpen={setCreateWebsiteDialogOpened} company={currentCompany} />
      </FormDialog>
    </>
  )
}
